import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Loader2, Crown, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * PaywallConversionCard — SuperAdmin view of the premium-paywall funnel
 * (`GET /api/analytics/paywall/summary`): impressions, click-through rate,
 * per-feature breakdown (which locked feature converts best), and audience
 * split (guests vs signed-in free users).
 */
const PaywallConversionCard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/analytics/paywall/summary`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load paywall analytics');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byEvent = data?.by_event || {};
  const shown = byEvent.shown || 0;
  const clicked = byEvent.clicked || 0;
  const ctr = data?.click_through_rate != null ? Math.round(data.click_through_rate * 100) : 0;
  const byFeature = data?.by_feature || {};
  const byAudience = data?.by_audience || {};
  const byVariant = data?.by_variant || {};

  const featureRows = Object.entries(byFeature)
    .map(([f, v]) => ({ f, shown: v.shown || 0, clicked: v.clicked || 0 }))
    .sort((a, b) => b.clicked - a.clicked);

  const variantRows = ['A', 'B']
    .map((v) => ({
      v,
      shown: byVariant[v]?.shown || 0,
      clicked: byVariant[v]?.clicked || 0,
      ctr: byVariant[v]?.ctr != null ? Math.round(byVariant[v].ctr * 100) : 0,
      label: v === 'A' ? 'A · “Unlock <feature>”' : 'B · “$4.99/mo”',
    }));
  const hasVariantData = variantRows.some((r) => r.shown > 0);
  const bestCtr = Math.max(...variantRows.map((r) => r.ctr));
  const winner = hasVariantData && variantRows.filter((r) => r.ctr === bestCtr && r.shown > 0).length === 1
    ? variantRows.find((r) => r.ctr === bestCtr && r.shown > 0)?.v
    : null;

  return (
    <Card className="border-2 border-amber-300/50 dark:border-amber-700/40" data-testid="paywall-conversion-card">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <Crown className="w-5 h-5" /> Premium Paywall — Conversion
          </span>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="paywall-conversion-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {loading && !data && (
          <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        )}
        {error && <p className="text-red-600 italic" data-testid="paywall-conversion-error">{error}</p>}
        {data && shown === 0 && (
          <p className="text-slate-500 italic" data-testid="paywall-conversion-empty">
            No paywall impressions yet. Once free users hit a Premium feature (AI Companion, premium audio, 3D journeys, Slidecon…), the funnel appears here.
          </p>
        )}
        {data && shown > 0 && (
          <>
            <div className="grid grid-cols-3 gap-3" data-testid="paywall-conversion-metrics">
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold">{shown}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Shown</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{clicked}</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Clicked</p>
              </div>
              <div className="rounded-lg bg-muted p-3 text-center">
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{ctr}%</p>
                <p className="text-[11px] uppercase tracking-widest text-slate-500">Click-through</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(byAudience).map(([a, n]) => (
                <Badge key={a} variant="secondary" className="capitalize">{a}: {n}</Badge>
              ))}
            </div>
            <div className="space-y-2" data-testid="paywall-conversion-features">
              <p className="text-[10px] uppercase tracking-widest text-slate-400">Which locked feature converts best</p>
              {featureRows.map(({ f, shown: s, clicked: c }) => {
                const pct = s > 0 ? Math.round((c / s) * 100) : 0;
                return (
                  <div key={f} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate pr-2">{f}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">{c}/{s} · {pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
            {hasVariantData && (
              <div className="space-y-2 pt-2 border-t border-amber-200/40 dark:border-amber-800/30" data-testid="paywall-variant-breakdown">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-2">
                  <span>A/B Upgrade Copy</span>
                  {winner && (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]" data-testid="paywall-variant-winner">
                      Variant {winner} winning
                    </Badge>
                  )}
                </div>
                {variantRows.map(({ v, shown: s, clicked: c, ctr: vc, label }) => (
                  <div key={v} className="space-y-1" data-testid={`paywall-variant-${v}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium truncate pr-2 text-xs">{label}</span>
                      <span className="text-xs text-slate-500 flex-shrink-0">{c}/{s} · {vc}%</span>
                    </div>
                    <div className="w-full h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div className={`h-full transition-all ${winner === v ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${vc}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PaywallConversionCard;
